import React from "react"
import tw, { styled, css } from "twin.macro"
import { useTranslation } from "next-i18next"
import { CardCvcElement, CardExpiryElement, CardNumberElement } from "@stripe/react-stripe-js"
import { MockCardCvcElement, MockCardExpiryElement, MockCardNumberElement } from "../../paymentGate/mocks"
import { HelpText } from "../input"
import { ReactComponent as AmexSVG } from "../../../assets/payment/cardIcons/amex.svg"
import { ReactComponent as DinersClubSVG } from "../../../assets/payment/cardIcons/diners_club.svg"
import { ReactComponent as DiscoverSVG } from "../../../assets/payment/cardIcons/discover.svg"
import { ReactComponent as JcbSVG } from "../../../assets/payment/cardIcons/jcb.svg"
import { ReactComponent as MastercardSVG } from "../../../assets/payment/cardIcons/mastercard.svg"
import { ReactComponent as UnionPaySVG } from "../../../assets/payment/cardIcons/unionpay.svg"
import { ReactComponent as VisaSVG } from "../../../assets/payment/cardIcons/visa.svg"
import { Button, ButtonTheme } from "../button"
import { UserOnGetUserFragmentFragment } from "../../../graphql/generated/graphql"

const StyledStripe = styled.div(() => [
  tw`space-y-1.5 max-w-screen-mobile-lg min-w-card`,
  css`
    input::placeholder {
      ${tw`text-md text-base-4`}
    }
    .cardElementField {
      width: 22%;
      ${tw`py-3 px-2 text-md`}
    }
    .cardElementField:nth-child(1) {
      ${tw`w-full`}
    }
    .cardElementField:nth-child(2) {
      width: 37%;
    }
  `,
])

interface CardBrandProps {
  cardBrand: string
  isStaff: boolean
}

export enum CardType {
  Visa = "visa",
  Amex = "amex",
  Mastercard = "mastercard",
  Discover = "discover",
  DinersClub = "diners_club",
  UnionPay = "unionpay",
  Jcb = "jcb",
}

export const CardBrandIcon = ({ cardBrand, isStaff }: CardBrandProps) => {
  switch (cardBrand) {
    case CardType.Amex:
      return <AmexSVG css={[isStaff && tw`opacity-40`]} />
    case CardType.DinersClub:
      return <DinersClubSVG css={[isStaff && tw`opacity-40`]} />
    case CardType.Discover:
      return <DiscoverSVG css={[isStaff && tw`opacity-40`]} />
    case CardType.Jcb:
      return <JcbSVG css={[isStaff && tw`opacity-40`]} />
    case CardType.Mastercard:
      return <MastercardSVG css={[isStaff && tw`opacity-40`]} />
    case CardType.Visa:
      return <VisaSVG css={[isStaff && tw`opacity-40`]} />
    case CardType.UnionPay:
      return <UnionPaySVG css={[isStaff && tw`opacity-40`]} />
    default:
      return <></>
  }
}

const StripeFields = ({ handleFocus, helpText, invalid }: CreditCardProps) => {
  const isStripeTestMode = process.env.ENV === "testing"

  const styles = {
    style: {
      base: {
        color: "--base-6",
        fontFamily: "Jeko, Helvetica, Arial, sans-serif",
        "::placeholder": {
          color: "--base-4",
        },
      },
      invalid: {
        iconColor: "--red",
        color: "--red",
      },
    },
  }

  return (
    <>
      {isStripeTestMode ? (
        MockCardNumberElement({})
      ) : (
        <CardNumberElement
          className="cardElementField"
          id="card-number"
          onFocus={() => {
            handleFocus?.()
          }}
          options={styles}
        />
      )}
      {isStripeTestMode ? (
        MockCardExpiryElement({})
      ) : (
        <CardExpiryElement
          className="cardElementField"
          id="card-expiry"
          onFocus={() => {
            handleFocus?.()
          }}
          options={styles}
        />
      )}
      {isStripeTestMode ? (
        MockCardCvcElement({})
      ) : (
        <CardCvcElement
          className="cardElementField"
          id="card-cvc"
          onFocus={() => {
            handleFocus?.()
          }}
          options={styles}
        />
      )}
      {helpText && (
        <HelpText data-testid={`${name}-help-text`} invalid={invalid}>
          {helpText}
        </HelpText>
      )}
    </>
  )
}

const SavedFields = (props: CreditCardProps) => {
  const last4 = props.user.savedPaymentMethod?.last4
  const cardType = props.user.savedPaymentMethod?.cardType ?? ""
  const { handleRemove, disabled } = props

  if (!handleRemove) {
    return <></>
  }

  return (
    <div tw="py-1 pl-2 flex justify-between w-full" data-testid="saved-payment">
      <div
        tw="flex items-center space-x-2"
        onFocus={() => {
          props.handleFocus?.()
        }}
      >
        <CardBrandIcon cardBrand={cardType} isStaff={false} />
        <p tw="text-md text-center tracking-wider" data-testid="last4">
          &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {last4}
        </p>
      </div>
      <Button
        dataTestId="delete-saved-payment"
        twStyle={tw`py-2 px-6 text-md tablet-lg:(px-4 py-1 text-xs)`}
        theme={ButtonTheme.WarningGhost}
        onClickAsync={async (e: React.MouseEvent) => {
          e.preventDefault()
          await handleRemove()
        }}
        isDisabled={disabled}
        action="delete"
      />
    </div>
  )
}
interface CreditCardProps {
  saved: boolean
  handleFocus?: () => void
  handleRemove?: () => void
  helpText?: string
  invalid: boolean
  disabled: boolean
  user: UserOnGetUserFragmentFragment
}

const CreditCard = (props: CreditCardProps) => {
  const { saved } = props
  const { t } = useTranslation("editProfile")

  return (
    <StyledStripe data-testid="payment-form">
      <div>
        <div tw="text-md font-bold">{t("settings.payment_info.label")}</div>
        <p tw="m-0 text-sm text-base-4">{t("settings.payment_info.description")}</p>
      </div>
      <div tw="w-full border border-base-3 rounded text-base-6 flex justify-between items-center">
        {saved ? <SavedFields {...props} /> : <StripeFields {...props} />}
      </div>
    </StyledStripe>
  )
}

export default CreditCard
